// Remix Screen
// author rodolfo lopez pintor <rlp@nebular.tv>
//////////////////////////////////////////////////////////////

import m from "mithril";

import {
	defineWidget, CardieWidget, ButtonNew, NoticeWidget, Notification, Empty
} from "@nebularstreams/libmui"

import {setLivePlaylist, loadContent, startTimerForRefresh} from "../../Api";

import {STATE_REMIX as STATE} from "../../State";

import {NanoPlayer, CustomNanoPlayer} from "../misc/NanoPlayer";
import {StreamCard} from "../misc/StreamCard";
import {TitleBarWidget} from "../misc/TitleBarWidget";

import AvailableSetlists from "./AvailableSetlists";
import PersistedSetlist from "./PersistedSetlist";
import VideoContent, {ContentItem} from "./VideoBucketList";

/** whether to enable live streams functionality */
const ENABLE_LIVE_STREAMS = false;

//////////////////////////////////////////////////////////////
// REMIX SCREEN: STRINGS

const STR = {
	REMIX_LIVE: "YOUR REMIX IS LIVE",
	REMIX_OFFLINE: "YOUR REMIX IS OFFLINE",
	CHANGE_STREAM: "change stream",
	CARD_LIVESTREAM: "Your reMix Live Stream",
	CARD_LIVESTREAM_SOURCE: "Live Stream Sources",
	CARD_LIVESTREAM_SOURCE_HELP: "Type a streamname to create a reMix Source for that live-stream. The stream has to be online. You can then drag and drop it into your current reMix",
	CARD_LIVESTREAM_SOURCE_HELP_EXISTING: "Drag and Drop this livestream into your current reMix. Make sure it remains online through your session",
	NO_SETLIST: "No current reMix. Please select a reMix or create a new one",
	TITLE: "reMix - nanoStream Studio"
};
//////////////////////////////////////////////////////////////

const {service: SERVICE} = STATE;

//////////////////////////////////////////////////////////////

/**
 * load the remix playlist
 * @param streamname
 * @private
 */

function loadLivePlaylist(streamname) {

	setLivePlaylist(streamname)
		.then(() => loadBucket(streamname))
		.catch((e) => {
			STATE.error = e.message;
			m.redraw();
		})

}

/**
 * load the videos in bucket
 * @param streamname
 * @private
 */

function loadBucket(streamname) {


	if (STATE.streamname === streamname && STATE.content?.list?.length) {
		m.redraw();
		return;
	}

	STATE.loadingContent = true;
	m.redraw();

	loadContent(STATE, streamname, "mp4")
		.catch((e) => {
			Notification.add(e.message);
		})
		.finally(() => {
			STATE.loadingContent = false;
			m.redraw()
		});
}

//////////////////////////////////////////////////////////////
// UI WIDGETS

const

	///////////////////////////////////////////////////////////
	//REMIX MAIN SCREEEN

	// widget: Main Screen
	RemixScreen = defineWidget(
		({attrs}) => [
			m(TitleBarWidget, {
				title: "reMix",
				rootTitle: "Studio",
				rootTitleClick: () => m.route.set(`/${attrs.orga}`)
			}),
			m(
				".flexcol.h-100.ohidden.grows.scrolly.outercontent",
				{
					class: STATE.mainBusy ? "mainbusy" : ""
				},
				m(".flexcol.grows.noshrink.innercontent", [

					m(CardieWidget,
						{
							class: "status",
							nofold: true,
							element: "div"
						},

						// first card
						STATE.streamname
							? [
								m(".streamname.fixstreamname", STATE.streamname),

								...STATE.livePlaylistStatus?.playing
									? [
										m(NoticeWidget, {
											icon: "broadcast-tower",
											text: STR.REMIX_LIVE,
											class: "onlinestatus online"
										})
									]
									: [
										m(NoticeWidget, {
											icon: "info-circle",
											text: STR.REMIX_OFFLINE,
											class: "onlinestatus offline"
										}),
										m(".flexrow.h-100", [
											m(ButtonNew, {
												class: "orangebutton h-100",
												icon: "sync",
												text: STR.CHANGE_STREAM,
												onClick: () => {
													delete STATE.streamname;
													STATE.content = {};
													delete STATE.currentItem;
													m.route.set(`/${attrs.orga}/${SERVICE}`)
												}
											}),
										])
									]
							]
							: [
								m(StreamCard, {state: STATE, service: SERVICE, orga: attrs.orga, class: "grows"})
							]
					),

					// body
					...STATE.streamname
						? [
							m(".flexrow.ohidden.maincontainer.grows.remixscreen", [

								// If there is stuff in the bucker, or if there is a setlist playling

								...(STATE.livePlaylistStatus?.playing || true)
									? [
										m(".flexcol.grows.ohidden.part-left", [

											// if there's a setlist playing, show nanoplayer
											...STATE.livePlaylistStatus?.playing
												? [
													m(NanoPlayer, {
														state: STATE,
														title: STR.CARD_LIVESTREAM
													})
												]
												: [
													// available setlists if not playing
													m(AvailableSetlists, {orga: attrs.orga}),
												],
											// Always show bucket list
											m(VideoContent, {orga: attrs.orga, state:STATE}),

											...ENABLE_LIVE_STREAMS
												? [
													m(CustomNanoPlayer, {
														title: STR.CARD_LIVESTREAM_SOURCE,
														state: STATE.liveStreamSources,
														help: STR.CARD_LIVESTREAM_SOURCE_HELP,
														helpExisting: STR.CARD_LIVESTREAM_SOURCE_HELP_EXISTING,
														liveStreamItem: ContentItem,
														orga: attrs.orga
													})
												]
												: [],
										]),
									]
									: [],


								// right column
								m(".flexcol.grows.ohidden.part-right.h-100", [

									// show error if there is such
									...STATE.error
										? [
											m(NoticeWidget, {
												class: "rednotice marg-2 grows",
												icon: "exclamation-triangle",
												text: STATE.error,
												onClick: () => {
													if (STATE.errorFatal) delete STATE.streamname;
													delete STATE.error;
													m.redraw();
												}
											})
										]
										: [],

									// main setlist window
									...STATE.livePlaylistStatus?.playing
										? [m(PersistedSetlist, {
											setlist: STATE.livePlaylist,
											status: STATE.livePlaylistStatus,
											current: true
										})]
										: STATE.currentSetlist
											? [m(PersistedSetlist, {
												setlist: STATE.currentSetlist,
												status: STATE.livePlaylistStatus
											})]
											: STATE.loadingSetlist
												? [
													m(Empty, {icon: "sync.fa-spin", size: 2, class: "pad-2"})
												]
												: [
													m(NoticeWidget, {
														icon: "info-circle",
														text: STR.NO_SETLIST,
														class: "help"
													})
												]
								])
							])
						] : []
				])
			)
		],
		({attrs}) => {
			if (attrs.key) {
				loadLivePlaylist(attrs.key);
			} else if (STATE.streamname) {
				m.route.set(`/${attrs.orga}/${SERVICE}/${STATE.streamname}`);
			}
			document.title = STR.TITLE
		},
		undefined,
		() => startTimerForRefresh(false)
	);

export default RemixScreen;
